import { MedStatusBadge } from "@/components/meds/med-status-badge";
import { PublicationIcon } from "@/svg/sections/publication-icon";
import { VademecumIcon } from "@/svg/sections/vademecum-icon";
import { ClinicalCaseIcon } from "@/svg/sections/clinical-case-icon";
import { CourseIcon } from "@/svg/sections/course-icon";
import { EbookIcon } from "@/svg/sections/ebook-icon";
import { ResidencyEncyclopediaIcon } from "@/svg/sections/residency-encyclopedia-icon";
import { VideoIcon } from "@/svg/sections/video-icon";

export const mapViewModelToItem = (
  section: HistoryAndFavouriteSection,
  item: HistoryAndFavouriteRawItem,
) => {
  const map: Record<HistoryAndFavouriteSection, any> = {
    icd10: (item: Icd10PreviewVM): HistoryAndSectionItem[] => ({
      title: item.name,
      slug: item.slug,
      category: {
        slug: item?.parent?.slug,
      },
      icon: (
        <div className="inline-flex h-6 w-9 flex-col items-center justify-center gap-2.5 rounded-md bg-indigo-900 px-1">
          <div className="justify-center text-xs leading-none font-semibold text-white">
            {item.code}
          </div>
        </div>
      ),
    }),
    calculator: (item: CalculatorPreviewVM) => ({
      title: item.title,
      slug: item.slug,
      category: {
        svgContent: item.category?.iconSvgContent ?? "",
        slug: item.category?.slug,
      },
      favourite: {
        resourceType: "calculator",
        resourceId: item.id,
      },
    }),
    tutorial: (item: TutorialPreviewVM) => ({
      title: item.title,
      slug: item.slug,
      category: item.category,
      favourite: {
        resourceType: "tutorial",
        resourceId: item.id,
      },
    }),
    fast_question: (item: TutorialPreviewVM) => ({
      title: item.title,
      slug: item.slug,
      category: item.category,
      favourite: {
        resourceType: "fast_question",
        resourceId: item.id,
      },
    }),
    meds: (group: MedGroupPreviewVM) => ({
      ...group,
      icon: <MedStatusBadge size={"xs"} status={group.status} />,
      favourite: {
        resourceType: "med",
        resourceId: group.id,
      },
    }),
    article: (article: ArticlePreviewVM) => ({
      ...article,
      icon: article.isVademecum ? <VademecumIcon /> : <PublicationIcon />,
      favourite: {
        resourceType: "article",
        resourceId: article.id,
      },
    }),
    medical_supply: (supply: MedicalSupplyVM) => ({
      title: `${supply.code} - ${supply.name}`,
      slug: supply.slug,
      favourite: {
        resourceType: "medical_supply",
        resourceId: supply.id,
      },
    }),
    clinical_case: (item: ClinicalCasePreviewVM) => ({
      title: item.name,
      slug: item.slug,
      category: {
        slug: item.category?.slug,
      },
      icon: <ClinicalCaseIcon />,
      favourite: {
        resourceType: "clinical_case",
        resourceId: item.id,
      },
    }),
    course: (item: CoursePreviewVM) => ({
      title: item.title,
      slug: item.slug,
      id: item.id,
      icon: <CourseIcon />,
      favourite: {
        resourceType: "course",
        resourceId: item.id,
      },
    }),
    ebook: (item: EbookPreviewVM) => ({
      title: item.name,
      slug: item.slug,
      icon: <EbookIcon />,
      favourite: {
        resourceType: "ebook",
        resourceId: item.id,
      },
    }),
    residency_specialization: (item: ResidencySpecializationPreviewVM) => ({
      title: item.title,
      slug: item.slug,
      icon: <ResidencyEncyclopediaIcon />,
      favourite: {
        resourceType: "residency_specialization",
        resourceId: item.id,
      },
    }),
    video: (item: PlaylistTreatedAsVideoPreviewVM) => ({
      title: item.title,
      slug: item.slug,
      id: item.id,
      icon: <VideoIcon />,
      favourite: {
        resourceType: "video",
        resourceId: item.id,
      },
    }),
    playlist: (item: PlaylistPreviewVM) => ({
      title: item.title,
      slug: item.slug,
      id: item.id,
      icon: <VideoIcon />,
      favourite: {
        resourceType: "video",
        resourceId: item.id,
      },
    }),
    playlist_episode: (item: PlaylistEpisodePreviewVM) => ({
      title: item.title,
      slug: item.slug,
      id: item.id,
      playlist: item.playlist,
      icon: <VideoIcon />,
      favourite: {
        resourceType: "video",
        resourceId: item.id,
      },
    }),
  };

  if (!item) {
    return null;
  }

  return map[section](item);
};
